import React from 'react';
import { Car, Route, Clock, AlertTriangle, CheckCircle2, MapPin, Flag } from 'lucide-react';
import { Badge } from '../ui/badge';
import { RouteMap } from './RouteMap';

export const fmtKm = (km) => `${Number(km || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
const fmtClock = (iso) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDur = (s) => { const m = Math.max(1, Math.round((s || 0) / 60)); return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}` : `${m} min`; };

const ADDRESS_FALLBACK = {
  missing: 'Adresse indisponible (aucune position GPS enregistrée)',
  error: 'Adresse en attente : elle sera complétée automatiquement dès que le service de cartographie répond',
};

/** Lieux de départ et d'arrivée (géocodage inverse mémorisé à la fin de l'accompagnement), affichés sous la carte. */
function TripAddresses({ sessions }) {
  const done = sessions.filter((s) => !s.live);
  if (done.length === 0) return null;
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2 space-y-2 text-xs" data-testid="acc-trip-addresses">
      {done.map((s) => {
        const fallback = !s.start_address && !s.end_address ? ADDRESS_FALLBACK[s.address_status] || ADDRESS_FALLBACK.error : null;
        return (
          <div key={s.id} data-testid={`acc-trip-addresses-${s.id}`}>
            {done.length > 1 && <p className="font-medium text-muted-foreground mb-1">Accompagnement {fmtClock(s.started_at)} → {fmtClock(s.ended_at)}</p>}
            {fallback ? (
              <p className="text-muted-foreground italic" data-testid={`acc-trip-address-fallback-${s.id}`}>{fallback}</p>
            ) : (
              <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
                <dt className="flex items-center gap-1 text-muted-foreground"><MapPin className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Départ</dt>
                <dd className="font-medium break-words" data-testid={`acc-trip-start-address-${s.id}`}>{s.start_address || <span className="text-muted-foreground italic font-normal">Adresse de départ indisponible</span>}</dd>
                <dt className="flex items-center gap-1 text-muted-foreground"><Flag className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Arrivée</dt>
                <dd className="font-medium break-words" data-testid={`acc-trip-end-address-${s.id}`}>{s.end_address || <span className="text-muted-foreground italic font-normal">Adresse d'arrivée indisponible</span>}</dd>
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
}

const LINE = {
  ok: { label: 'Ligne PAFK sur le bon', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30', icon: CheckCircle2 },
  partial: { label: 'Km dans le libellé', cls: 'bg-[hsl(var(--brand-apricot))]/20 border-[hsl(var(--brand-apricot))]', icon: AlertTriangle },
  error: { label: 'Ligne non créée', cls: 'bg-destructive/10 text-destructive border-destructive/30', icon: AlertTriangle },
  skipped: { label: 'Sans km', cls: 'bg-secondary text-muted-foreground border-border', icon: null },
};

/** Tracés GPS des accompagnements (bouton ACC) d'une prestation : liste + carte. `sessions` = sessions avec `points`. */
export function AccTrips({ sessions = [], active = null, height = 220, compact = false }) {
  const all = [...(active ? [{ ...active, live: true }] : []), ...sessions];
  if (all.length === 0) return null;
  const routes = all.filter((s) => (s.points || []).length > 1).map((s) => ({ id: s.id, points: s.points, color: s.live ? '#dc2626' : s.color || '#2f5d4a', label: s.label, live: !!s.live }));
  return (
    <div className="space-y-2" data-testid="acc-trips">
      <ul className={`divide-y divide-border rounded-xl border border-border ${compact ? 'text-xs' : 'text-sm'}`}>
        {all.map((s) => {
          const st = s.live ? null : LINE[s.line_status] || null;
          const Icon = st?.icon;
          return (
            <li key={s.id} className="px-3 py-2 flex items-start justify-between gap-2" data-testid={`acc-trip-${s.id}`}>
              <div className="min-w-0">
                <p className="font-medium flex items-center gap-1.5"><Car className={`h-3.5 w-3.5 ${s.live ? 'text-destructive' : 'text-[hsl(var(--accent))]'}`} /> {s.live ? 'Accompagnement en cours' : `Accompagnement ${fmtClock(s.started_at)} → ${fmtClock(s.ended_at)}`}</p>
                <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-xs">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDur(s.live ? (Date.now() - new Date(s.started_at).getTime()) / 1000 : s.duration_s)}</span>
                  <span className="flex items-center gap-1"><Route className="h-3 w-3" /> GPS {fmtKm(s.km_measured ?? (s.distance_m || 0) / 1000)}</span>
                  {s.comment && <span>· {s.comment}</span>}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                {!s.live && <p className="font-semibold" data-testid={`acc-trip-km-${s.id}`}>{fmtKm(s.km_billed)}</p>}
                {st && <Badge className={`border text-[10px] ${st.cls}`}>{Icon && <Icon className="h-3 w-3 mr-1" />}{st.label}</Badge>}
                {s.live && <Badge className="border text-[10px] bg-destructive/10 text-destructive border-destructive/30 animate-pulse">En direct</Badge>}
              </div>
            </li>
          );
        })}
      </ul>
      {routes.length > 0 ? (
        <RouteMap routes={routes} height={height} focusId={routes[0].id} className="rounded-xl overflow-hidden border border-border" />
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="acc-trips-nomap">Pas de tracé GPS enregistré pour cet accompagnement (position indisponible ou kilomètres saisis à la main).</p>
      )}
      <TripAddresses sessions={all} />
    </div>
  );
}
